import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Box,
  useTheme,
  Grid
} from '@mui/material';
import axios from 'axios';

interface ParentsGuideMediaProps {
  open: boolean;
  onClose: () => void;
}

interface MediaItem {
  id: number;
  title?: string;
  name?: string;
  overview?: string;
  poster_path?: string;
  release_date?: string;
  media_type?: string;
}

const ParentsGuideMedia = ({ open, onClose }: ParentsGuideMediaProps) => {
  const theme = useTheme();

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<MediaItem[]>([]);
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const imageBaseUrl = 'https://image.tmdb.org/t/p/';
  const imageSize = 'w185';

  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setSearchResults([]);
      setSelectedMedia(null);
      setError(null);
    }
  }, [open]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`https://api.themoviedb.org/3/search/multi`, {
        params: {
          api_key: process.env.REACT_APP_TMDB_API_KEY,
          query: searchTerm,
        },
      });
      setSearchResults(response.data.results || []);
    } catch (err) {
      console.error('Error fetching media data:', err);
      setError('Failed to fetch media data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMedia = async (media: MediaItem) => {
    setSelectedMedia(media);

    try {
      const response = await axios.get(
        `https://api.themoviedb.org/3/${media.media_type}/${media.id}/external_ids`,
        {
          params: {
            api_key: process.env.REACT_APP_TMDB_API_KEY,
          },
        }
      );

      const imdbId = response.data.imdb_id;
      if (imdbId) {
        const imdbParentsGuideUrl = `https://www.imdb.com/title/${imdbId}/parentalguide`;

        const link = document.createElement('a');
        link.href = imdbParentsGuideUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Error fetching IMDb info:', err);
      setError('Failed to open IMDb Parents Guide.');
    }

    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          fontWeight: 600,
          color: theme.palette.primary.main,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        Search Media for Parents Guide
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Search for a movie or TV show"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            sx={{ mt: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Search'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {selectedMedia && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Selected Media:
            </Typography>
            <Box display="flex" flexDirection="column" alignItems="center">
              <Box
                component="img"
                src={
                  selectedMedia.poster_path
                    ? `${imageBaseUrl}${imageSize}${selectedMedia.poster_path}`
                    : '/path/to/default-image.jpg'
                }
                alt={selectedMedia.title || selectedMedia.name}
                sx={{
                  width: 150,
                  borderRadius: 1,
                  mb: 2,
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'scale(1.05)' },
                }}
              />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {selectedMedia.overview}
              </Typography>
            </Box>
          </Box>
        )}

        {!selectedMedia && searchResults.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Search Results:
            </Typography>

            <Grid
              container
              spacing={2}
              justifyContent="center"
              sx={{ maxHeight: 400, overflowY: 'auto' }}
            >
              {searchResults.map((media) => (
                <Grid
                  key={media.id}
                  size={{ xs: 6, sm: 4, md: 3, lg: 2 }}
                  sx={{
                    width: '100%',
                    maxWidth: 150,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleSelectMedia(media)}
                >
                  <Box
                    component="img"
                    src={
                      media.poster_path
                        ? `${imageBaseUrl}${imageSize}${media.poster_path}`
                        : '/path/to/default-image.jpg'
                    }
                    alt={media.title || media.name}
                    sx={{
                      width: '100%',
                      borderRadius: 1,
                      mb: 1,
                      transition: 'transform 0.2s',
                      '&:hover': { transform: 'scale(1.05)' },
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'center',
                      color: 'text.primary',
                      fontSize: '0.85rem',
                    }}
                  >
                    {media.title || media.name}
                    {media.release_date && ` (${media.release_date.split('-')[0]})`}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button onClick={onClose} color="secondary" variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ParentsGuideMedia;